import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Mono hint (shortcut) after the content. */
  shortcut?: string;
  delayDuration?: number;
  /** Controlled open state (both props together); uncontrolled when omitted. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

/** Wrap an app once so tooltips share timing. */
export function TooltipProvider({ children, delayDuration = 300 }: { children: ReactNode; delayDuration?: number }) {
  return <RadixTooltip.Provider delayDuration={delayDuration}>{children}</RadixTooltip.Provider>;
}

/** Radix Tooltip: hairline --line panel on --card, 12px text, no arrow, no shadow. Shows on focus too. */
export function Tooltip({ content, children, side = 'top', shortcut, delayDuration, open, onOpenChange, className }: TooltipProps) {
  return (
    <RadixTooltip.Root delayDuration={delayDuration} open={open} onOpenChange={onOpenChange}>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          sideOffset={4}
          collisionPadding={8}
          className={cn('z-[100] flex max-w-[280px] items-center gap-2 rounded-sm border border-line bg-card px-2 py-1 text-xs text-ink', className)}
        >
          <span>{content}</span>
          {shortcut ? <span className="font-mono text-2xs text-mute">{shortcut}</span> : null}
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}
